import { useMemo } from 'react';
import { useAppSelector } from 'hooks';

/**
 * Whether a tournament is a FREEZEOUT (no re-entry / rebuy of any kind).
 *
 * The tournament FORMAT isn't carried on the in-game table state (`game.*`) or
 * the gotTnmStatus feed — it lives only on the lobby tournament list entries as
 * a `gameType` string ('freezeout' / 'freezout'), the same field
 * SaloonLobbyContainer's detectTournamentType reads. So we look the tournament
 * up in the lobby lists by id.
 *
 * Degrades safely: when the tournament isn't found (e.g. a reload straight into
 * a table that bypassed the lobby, so the lists are empty) it returns false —
 * i.e. "not known to be a Freezeout", which never suppresses re-entry on its
 * own; the caller's other eligibility signals still apply.
 *
 * Pure helper exported separately for unit testing.
 */
const FREEZEOUT_GAME_TYPES = new Set(['freezeout', 'freezout']);

/**
 * Whether a raw tournament `gameType` value denotes a Freezeout. Handles the
 * server's 'freezout' spelling variant and is case-insensitive. Exported so any
 * surface already holding the tournament record (e.g. the lobby's
 * TournamentDetailModal) can guard without re-looking-it-up.
 */
export function isFreezeoutGameType(gameType: unknown): boolean {
  return FREEZEOUT_GAME_TYPES.has(
    String(gameType ?? '')
      .trim()
      .toLowerCase()
  );
}

export function isFreezeoutTournament(
  tournamentId: unknown,
  tournamentList: Array<{ tournamentId?: unknown; gameType?: unknown }> | null | undefined,
  sitAndGoList: Array<{ tournamentId?: unknown; gameType?: unknown }> | null | undefined
): boolean {
  const tid = Number(tournamentId);
  if (!Number.isFinite(tid) || tid === 0) return false;
  const entry = [...(tournamentList || []), ...(sitAndGoList || [])].find(
    (t) => Number(t?.tournamentId) === tid
  );
  return isFreezeoutGameType(entry?.gameType);
}

/** React hook wrapper: resolves Freezeout-ness for the given tournament id. */
export function useIsFreezeout(tournamentId: unknown): boolean {
  const tournamentList = useAppSelector(
    (state) => (state as any).lobby?.tournament?.tournamentList
  );
  const sitAndGoList = useAppSelector((state) => (state as any).lobby?.tournament?.sitAndGoList);
  return useMemo(
    () => isFreezeoutTournament(tournamentId, tournamentList, sitAndGoList),
    [tournamentId, tournamentList, sitAndGoList]
  );
}
